/**
 * PWA Cache Manager - Handles persistent caching using Cache API and IndexedDB
 */

import { Book } from '@/types/book';
import { supabase } from '@/integrations/supabase/client';

interface CacheMetadata {
  bookId: number;
  cacheVersion: number;
  urls: string[];
  cachedAt: number;
  lastAccessed: number;
}

interface AssetCacheEntry {
  url: string;
  bookId?: number;
  cacheVersion?: number;
  cachedAt: number;
  type: 'cover' | 'media' | 'ui' | 'avatar';
}

interface OfflineBook {
  bookId: number;
  bookData: any;
  downloadedAt: number;
  size: number;
  assetCount: number; // total assets expected
  downloadedAssetCount?: number; // assets actually cached
  completed?: boolean; // marked true when SW finishes caching
}

class PWACacheManager {
  private dbName = 'bookstar-cache-db';
  private dbVersion = 1;
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    if (this.db) return;

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };
      
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Store for cache metadata
        if (!db.objectStoreNames.contains('cache_metadata')) {
          const metadataStore = db.createObjectStore('cache_metadata', { keyPath: 'bookId' });
          metadataStore.createIndex('cachedAt', 'cachedAt');
        }
        
        // Store for asset entries
        if (!db.objectStoreNames.contains('asset_cache')) {
          const assetStore = db.createObjectStore('asset_cache', { keyPath: 'url' });
          assetStore.createIndex('bookId', 'bookId');
          assetStore.createIndex('type', 'type');
        }
        
        // Store for offline books
        if (!db.objectStoreNames.contains('offline_books')) {
          db.createObjectStore('offline_books', { keyPath: 'bookId' });
        }
      };
    });
  }

  /**
   * Check if service worker is available
   */
  private isServiceWorkerAvailable(): boolean {
    if (!('serviceWorker' in navigator && 'caches' in window)) return false;
    
    // Never register SW in preview/iframe contexts
    try {
      if (window.self !== window.top) return false;
    } catch { return false; }
    
    const host = window.location.hostname;
    if (host.includes('id-preview--') || host.includes('lovableproject.com')) return false;
    
    return true;
  }

  /**
   * Register service worker
   */
  async registerServiceWorker(): Promise<void> {
    if (!this.isServiceWorkerAvailable()) {
      console.warn('[PWA] Service Worker not available in this environment');
      return;
    }

    // Extra guard: never register in iframe or preview hosts
    try {
      const isInIframe = window.self !== window.top;
      if (isInIframe) {
        console.warn('[PWA] Skipping SW registration inside iframe');
        return;
      }
    } catch {
      console.warn('[PWA] Skipping SW registration (cross-origin iframe)');
      return;
    }

    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('[PWA] Service Worker registered successfully');
      
      // Listen for updates
      registration.addEventListener('updatefound', () => {
        console.log('[PWA] Service Worker update found');
      });
    } catch (error) {
      console.error('[PWA] Service Worker registration failed:', error);
    }
  }

  /**
   * Preload only app shell and UI assets - NOT full books (iOS optimization)
   * Books will be streamed on demand
   */
  async preloadAllBookAssets(books: Book[]): Promise<void> {
    if (!this.isServiceWorkerAvailable()) return;

    console.log(`[PWA Cache] iOS optimization: Preloading thumbnails only, not book content`);
    
    // Only preload thumbnails/covers, not full images or audio
    const thumbnailUrls = books
      .map(book => book.cover_image_url)
      .filter(Boolean) as string[];
    
    const uniqueUrls = [...new Set(thumbnailUrls)];
    console.log(`[PWA Cache] Preloading ${uniqueUrls.length} thumbnails only`);
    
    // Smaller batches for iOS
    const batchSize = 5;
    for (let i = 0; i < uniqueUrls.length; i += batchSize) {
      const batch = uniqueUrls.slice(i, i + batchSize);
      await this.preloadAssetBatch(batch);
      // Delay between batches for iOS
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }

  /**
   * Extract all URLs from a book that need caching
   */
  private extractBookUrls(book: Book): string[] {
    const urls: string[] = [];
    
    console.log('[PWA Cache] Extracting URLs from book:', book.id);
    console.log('[PWA Cache] Book has compiled_book?', !!book.compiled_book);
    
    // Cover image
    if (book.cover_image_url) {
      urls.push(this.addCacheBusting(book.cover_image_url, book.cache_version));
      console.log('[PWA Cache] Added cover image');
    }
    
    // Page images (if compiled_book exists, extract from it)
    if (book.compiled_book && (typeof book.compiled_book === 'object' || typeof book.compiled_book === 'string')) {
      let compiledBook: any = book.compiled_book as any;
      if (typeof compiledBook === 'string') {
        try { compiledBook = JSON.parse(compiledBook); } catch {}
      }
      
      // Helper to extract a URL from string or object
      const pickUrl = (img: any): string | undefined => {
        if (!img) return undefined;
        if (typeof img === 'string') return img;
        if (typeof img === 'object') {
          if (img.url) return img.url as string;
          if (img.image_url) return img.image_url as string;
          if (img.src) return img.src as string;
        }
        return undefined;
      };
      
      // Page images array (objects containing image_url)
      if (compiledBook.pages && Array.isArray(compiledBook.pages)) {
        console.log('[PWA Cache] Found pages array with', compiledBook.pages.length, 'pages');
        compiledBook.pages.forEach((page: any, index: number) => {
          const u = pickUrl(page?.image_url ?? page?.image);
          if (u) urls.push(this.addCacheBusting(u, book.cache_version));
        });
      }
      
      // New format: compiledBook.images.pages
      if (compiledBook.images && Array.isArray(compiledBook.images.pages)) {
        console.log('[PWA Cache] Found images.pages with', compiledBook.images.pages.length, 'entries');
        compiledBook.images.pages.forEach((img: any) => {
          const u = pickUrl(img);
          if (u) urls.push(this.addCacheBusting(u, book.cache_version));
        });
      }
      
      // Legacy format: compiledBook.images as array
      if (compiledBook.images && Array.isArray(compiledBook.images)) {
        console.log('[PWA Cache] Found legacy images array with', compiledBook.images.length, 'entries');
        compiledBook.images.forEach((img: any) => {
          const u = pickUrl(img);
          if (u) urls.push(this.addCacheBusting(u, book.cache_version));
        });
      }
      
      // Images by page map
      if (compiledBook.imagesByPage && typeof compiledBook.imagesByPage === 'object') {
        const imageCount = Object.keys(compiledBook.imagesByPage).length;
        console.log('[PWA Cache] Found imagesByPage with', imageCount, 'entries');
        Object.values(compiledBook.imagesByPage).forEach((img: any) => {
          const u = pickUrl(img);
          if (u) urls.push(this.addCacheBusting(u, book.cache_version));
          const thumb = pickUrl(img?.thumbnail);
          if (thumb) urls.push(this.addCacheBusting(thumb, book.cache_version));
        });
      }
      
      // Audio per page
      if (compiledBook.audio?.pageAudioUrls) {
        const audioCount = Object.keys(compiledBook.audio.pageAudioUrls).length;
        console.log('[PWA Cache] Found audio for', audioCount, 'pages');
        Object.values(compiledBook.audio.pageAudioUrls).forEach((audioUrl: any) => {
          const u = pickUrl(audioUrl);
          if (u) urls.push(this.addCacheBusting(u, book.cache_version));
        });
      }
    } else {
      console.warn('[PWA Cache] ⚠️ Book missing compiled_book - will only download cover!');
    }

    console.log('[PWA Cache] Total URLs extracted:', urls.length);
    return urls;
  }

  /**
   * Add cache busting parameters
   */
  private addCacheBusting(url: string, cacheVersion?: number): string {
    try {
      const urlObj = new URL(url);
      if (cacheVersion) {
        urlObj.searchParams.set('v', cacheVersion.toString());
      }
      urlObj.searchParams.set('pwa', '1');
      return urlObj.toString();
    } catch {
      return url;
    }
  }

  /**
   * Preload a batch of assets via service worker
   */
  private async preloadAssetBatch(urls: string[]): Promise<void> {
    if (!navigator.serviceWorker.controller) return;

    navigator.serviceWorker.controller.postMessage({
      type: 'PRELOAD_ASSETS',
      urls
    });
  }

  /**
   * Store cache metadata in IndexedDB
   */
  private async storeCacheMetadata(metadata: CacheMetadata): Promise<void> {
    if (!this.db) return;

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['cache_metadata'], 'readwrite');
      const store = transaction.objectStore('cache_metadata');
      
      store.put(metadata);
      
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  /**
   * Get cache metadata for a book
   */
  async getCacheMetadata(bookId: number): Promise<CacheMetadata | null> {
    if (!this.db) await this.init();
    if (!this.db) return null;

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['cache_metadata'], 'readonly');
      const store = transaction.objectStore('cache_metadata');
      const request = store.get(bookId);
      
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Check if book assets need updating
   */
  async shouldUpdateBookCache(book: Book): Promise<boolean> {
    const metadata = await this.getCacheMetadata(book.id);
    
    if (!metadata) return true; // No cache, need to update
    
    const currentVersion = book.cache_version || 1;
    return metadata.cacheVersion < currentVersion;
  }

  /**
   * Update cache for a specific book
   */
  async updateBookCache(book: Book): Promise<void> {
    console.log(`PWA Cache: Updating cache for book ${book.id}`);
    
    // Invalidate old cache via service worker
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'INVALIDATE_BOOK_CACHE',
        bookId: book.id,
        cacheVersion: book.cache_version
      });
    }

    // Preload new assets
    const urls = this.extractBookUrls(book);
    await this.preloadAssetBatch(urls);
    
    // Update metadata
    await this.storeCacheMetadata({
      bookId: book.id,
      cacheVersion: book.cache_version || 1,
      urls,
      cachedAt: Date.now(),
      lastAccessed: Date.now()
    });
  }

  /**
   * Check if app is offline
   */
  isOffline(): boolean {
    return !navigator.onLine;
  }

  /**
   * Get cache stats
   */
  async getCacheStats(): Promise<{ cacheSize: number; totalAssets: number; lastUpdate: number }> {
    if (!this.isServiceWorkerAvailable()) {
      return { cacheSize: 0, totalAssets: 0, lastUpdate: 0 };
    }

    try {
      const cacheNames = await caches.keys();
      let totalSize = 0;
      let totalAssets = 0;
      let lastUpdate = 0;

      for (const cacheName of cacheNames) {
        const cache = await caches.open(cacheName);
        const requests = await cache.keys();
        totalAssets += requests.length;
        
        // Estimate size (rough calculation)
        for (const request of requests) {
          try {
            const response = await cache.match(request);
            if (response && response.headers.get('content-length')) {
              totalSize += parseInt(response.headers.get('content-length') || '0');
            }
          } catch (e) {
            // Ignore errors
          }
        }
      }

      return { cacheSize: totalSize, totalAssets, lastUpdate };
    } catch (error) {
      console.error('Error getting cache stats:', error);
      return { cacheSize: 0, totalAssets: 0, lastUpdate: 0 };
    }
  }

  /**
   * Clear all caches (emergency reset)
   */
  async clearAllCaches(): Promise<void> {
    if (!this.isServiceWorkerAvailable()) return;

    try {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
      
      // Clear IndexedDB
      if (this.db) {
        this.db.close();
        await new Promise((resolve, reject) => {
          const deleteReq = indexedDB.deleteDatabase(this.dbName);
          deleteReq.onsuccess = () => resolve(undefined);
          deleteReq.onerror = () => reject(deleteReq.error);
        });
        this.db = null;
      }
      
      console.log('PWA Cache: All caches cleared');
    } catch (error) {
      console.error('Error clearing caches:', error);
    }
  }

  /**
   * Force update by clearing caches and reloading
   * Useful when app is stuck on old version
   */
  async forceUpdate(): Promise<void> {
    try {
      console.log('[PWA Cache] Forcing update...');
      
      // Send message to service worker to clear caches
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'FORCE_UPDATE' });
      }
      
      // Wait briefly for SW to clear caches
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Reload the page
      window.location.reload();
    } catch (error) {
      console.error('Error forcing update:', error);
      throw error;
    }
  }

  // ============ Offline Book Management ============

  /**
   * Download a book for offline reading
   */
  async downloadBookForOffline(book: Book): Promise<{ success: boolean; assetCount: number; size: number }> {
    await this.init();
    
    try {
      console.log('Downloading book for offline:', book.id);
      
      // Ensure we have essential fields including compiled_book with images
      // V1 books table retired — use whatever the caller provided.
      const enrichedBook: Book = { ...book } as Book;


      
      // Extract all URLs (now includes images from compiled_book)
      const urls = this.extractBookUrls(enrichedBook);
      console.log('[PWA Cache] Extracted URLs for offline:', urls.length, 'assets');
      
      if (urls.length === 0 || urls.length === 1) {
        console.warn('[PWA Cache] Very few URLs extracted - book may not have compiled_book data');
      }
      
      // Estimate size (rough calculation)
      const estimatedSize = urls.length * 500000; // ~500KB per asset
      
      // Check storage quota
      if ('storage' in navigator && 'estimate' in navigator.storage) {
        const estimate = await navigator.storage.estimate();
        const available = (estimate.quota || 0) - (estimate.usage || 0);
        
        if (available < estimatedSize * 1.2) {
          throw new Error('Insufficient storage space');
        }
      }
      
      // Send download request to service worker
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'DOWNLOAD_BOOK_FOR_OFFLINE',
          bookId: book.id,
          urls
        });
      }
      
      // Store book data in IndexedDB
      const offlineBook: OfflineBook = {
        bookId: book.id,
        bookData: enrichedBook,
        downloadedAt: Date.now(),
        size: estimatedSize,
        assetCount: urls.length,
        downloadedAssetCount: 0,
        completed: false
      };
      
      await this.storeOfflineBook(offlineBook);
      
      console.log('Book saved for offline:', book.id, urls.length, 'assets');
      
      return {
        success: true,
        assetCount: urls.length,
        size: estimatedSize
      };
    } catch (error) {
      console.error('Error downloading book for offline:', error);
      throw error;
    }
  }

  /**
   * Store offline book in IndexedDB
   */
  private async storeOfflineBook(offlineBook: OfflineBook): Promise<void> {
    if (!this.db) return;

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['offline_books'], 'readwrite');
      const store = transaction.objectStore('offline_books');
      
      store.put(offlineBook);
      
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  /**
   * Get offline book from IndexedDB
   */
  async getOfflineBook(bookId: number): Promise<any | null> {
    await this.init();
    if (!this.db) return null;
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['offline_books'], 'readonly');
      const store = transaction.objectStore('offline_books');
      const request = store.get(bookId);
      
      request.onsuccess = () => {
        const result = request.result as OfflineBook | undefined;
        resolve(result?.bookData || null);
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Remove offline book
   */
  async removeOfflineBook(bookId: number): Promise<void> {
    await this.init();
    if (!this.db) return;
    
    try {
      // Remove from IndexedDB
      await new Promise<void>((resolve, reject) => {
        const transaction = this.db!.transaction(['offline_books'], 'readwrite');
        const store = transaction.objectStore('offline_books');
        store.delete(bookId);
        
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error);
      });
      
      // Tell service worker to remove cached assets
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'REMOVE_OFFLINE_BOOK',
          bookId
        });
      }
      
      console.log('Removed offline book:', bookId);
    } catch (error) {
      console.error('Error removing offline book:', error);
      throw error;
    }
  }

  /**
   * Mark offline book as completed
   */
  async markOfflineBookCompleted(bookId: number, downloadedAssetCount: number): Promise<void> {
    await this.init();
    if (!this.db) return;

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(['offline_books'], 'readwrite');
      const store = tx.objectStore('offline_books');
      const getReq = store.get(bookId);

      getReq.onsuccess = () => {
        const record = getReq.result as OfflineBook | undefined;
        if (record) {
          record.completed = true;
          record.downloadedAssetCount = downloadedAssetCount;
          store.put(record);
        }
      };

      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  /**
   * Get list of offline books
   */
  async getOfflineBookList(): Promise<OfflineBook[]> {
    await this.init();
    if (!this.db) return [];
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['offline_books'], 'readonly');
      const store = transaction.objectStore('offline_books');
      const request = store.getAll();
      
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Check if book is downloaded
   */
  async isBookDownloaded(bookId: number): Promise<boolean> {
    await this.init();
    if (!this.db) return false;
    
    return new Promise((resolve) => {
      const transaction = this.db!.transaction(['offline_books'], 'readonly');
      const store = transaction.objectStore('offline_books');
      const request = store.get(bookId);
      
      request.onsuccess = () => {
        const rec = request.result as OfflineBook | undefined;
        // Backward compatible: if 'completed' is undefined, treat as downloaded
        resolve(!!rec && (rec.completed === undefined ? true : rec.completed === true));
      };
      request.onerror = () => resolve(false);
    });
  }

  /**
   * Get storage estimate
   */
  async getStorageEstimate(): Promise<{ usage: number; quota: number; available: number }> {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      return {
        usage: estimate.usage || 0,
        quota: estimate.quota || 0,
        available: (estimate.quota || 0) - (estimate.usage || 0)
      };
    }
    
    return { usage: 0, quota: 0, available: 0 };
  }
}

export const pwaCacheManager = new PWACacheManager();